import { supabase } from '../lib/supabase';
import { Content, Conversation, Message, Model } from '@shared/types';
import {
  QueryClient,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

function messageSentConversationUpdate(
  newMessage: Message,
  conversationId: string,
) {
  return (oldConversations: Conversation[] | undefined) => {
    if (!oldConversations) return oldConversations;
    return oldConversations
      .map((conv) => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            current_message_leaf_id: newMessage.id,
            updated_at: newMessage.created_at,
          };
        }
        return conv;
      })
      .sort((a, b) => {
        return (
          new Date(b.updated_at ?? '').getTime() -
          new Date(a.updated_at ?? '').getTime()
        );
      });
  };
}

function messageInsertedConversationUpdate(
  queryClient: QueryClient,
  newMessage: Message,
  conversationId: string,
) {
  // Update the current conversation optimistically
  queryClient.setQueryData(
    ['conversation', conversationId],
    (oldConversation: Conversation) => ({
      ...oldConversation,
      current_message_leaf_id: newMessage.id,
    }),
  );

  // Update messages optimistically
  queryClient.setQueryData(
    ['messages', conversationId],
    (oldMessages: Message[] | undefined) => {
      if (!oldMessages || oldMessages.length === 0) return [newMessage];
      if (oldMessages.find((msg) => msg.id === newMessage.id)) {
        return oldMessages.map((msg) =>
          msg.id === newMessage.id ? newMessage : msg,
        );
      }
      return [...oldMessages, newMessage];
    },
  );

  // Update conversations list optimistically
  queryClient.setQueryData(
    ['conversations'],
    messageSentConversationUpdate(newMessage, conversationId),
  );
}

export function useMessagesQuery(conversationId: string | null) {
  return useQuery<Message[]>({
    enabled: !!conversationId,
    queryKey: ['messages', conversationId],
    staleTime: 30000, // Keep messages fresh for 30 seconds to prevent race conditions
    queryFn: async () => {
      if (!conversationId) return [];
      
      const { data: messagesData, error: messagesError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      return (messagesData || []) as Message[];
    },
  });
}

export function useInsertMessageMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      message: Omit<Message, 'id' | 'created_at'>,
    ) => {
      const { data, error } = await supabase
        .from('messages')
        .insert([{ ...message }])
        .select()
        .single();

      if (error) throw error;

      return data as Message;
    },
    onSuccess(newMessage) {
      messageInsertedConversationUpdate(
        queryClient,
        newMessage,
        newMessage.conversation_id,
      );
    },
    onError(error) {
      console.error(error);
    },
  });
}

export function useParametricChatMutation({
  conversationId,
}: {
  conversationId: string;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  return useMutation({
    mutationKey: ['parametric-chat', conversationId],
    mutationFn: async ({
      model,
      messageId,
      conversationId,
      thinking,
    }: {
      model: Model;
      messageId: string;
      conversationId: string;
      thinking?: boolean;
    }) => {
      const newMessageId = crypto.randomUUID();
      let initialized = false;

      // Get access token
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session?.access_token) {
        throw new Error('Not authenticated');
      }

      // Start streaming request
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${sessionData.session.access_token}`,
          },
          body: JSON.stringify({
            conversationId,
            messageId,
            model,
            newMessageId,
            thinking,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Network response was not ok: ${response.status} ${response.statusText}`,
        );
      }

      if (response.headers.get('Content-Type')?.includes('application/json')) {
        const data = await response.json();
        if (data.message) {
          return data.message;
        } else {
          throw new Error('No message received');
        }
      }

      async function initialize() {
        await queryClient.cancelQueries({
          queryKey: ['conversation', conversationId],
        });
        queryClient.setQueryData(
          ['conversation', conversationId],
          (oldConversation: Conversation) => ({
            ...oldConversation,
            current_message_leaf_id: newMessageId,
          }),
        );
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }

      const decoder = new TextDecoder();
      let leftover = '';
      let finalMessage: Message | null = null;
      let totalBytesReceived = 0;
      let parsedMessageCount = 0;

      console.log('[MessageService] Starting stream read');

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log(`[MessageService] Stream done. Total bytes: ${totalBytesReceived}, messages parsed: ${parsedMessageCount}`);
            break;
          }
          
          totalBytesReceived += value?.length || 0;

          leftover += decoder.decode(value, { stream: true });
          const lines = leftover.split('\n');
          leftover = lines.pop() ?? '';

          for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line) continue;
            try {
              const data: Message = JSON.parse(line);
              finalMessage = data;
              parsedMessageCount++;

              queryClient.setQueryData(
                ['messages', conversationId],
                (oldMessages: Message[] | undefined) => {
                  if (!oldMessages || oldMessages.length === 0) {
                    return [data];
                  }
                  if (oldMessages.find((msg) => msg.id === data.id)) {
                    return oldMessages.map((msg) =>
                      msg.id === data.id ? data : msg,
                    );
                  } else {
                    return [...oldMessages, data];
                  }
                },
              );

              if (!initialized) {
                await initialize();
                initialized = true;
              }
            } catch (parseError) {
              console.error('Error parsing streaming data:', parseError, 'Line content:', line.substring(0, 200));
            }
          }
        }

        // Flush decoder
        const flushRemainder = decoder.decode();
        if (flushRemainder) leftover += flushRemainder;
        const tail = leftover.trim();
        if (tail) {
          try {
            const data: Message = JSON.parse(tail);
            finalMessage = data;
            queryClient.setQueryData(
              ['messages', conversationId],
              (oldMessages: Message[] | undefined) => {
                if (!oldMessages || oldMessages.length === 0) {
                  return [data];
                }
                if (oldMessages.find((msg) => msg.id === data.id)) {
                  return oldMessages.map((msg) =>
                    msg.id === data.id ? data : msg,
                  );
                } else {
                  return [...oldMessages, data];
                }
              },
            );
          } catch (parseError) {
            console.error('Error parsing final streaming data:', parseError);
          }
        }
      } finally {
        reader.releaseLock();
      }

      if (!finalMessage) {
        console.error('Stream completed but no valid message received. Leftover buffer:', leftover);
        throw new Error('No final message received - the server may be experiencing issues. Please try again.');
      }

      return finalMessage;
    },
    onSuccess: (newMessage, variables) => {
      console.log('[MessageService] Chat success, final message:', {
        id: newMessage.id,
        hasArtifact: !!newMessage.content?.artifact,
        hasCode: !!newMessage.content?.artifact?.code,
        codeLength: newMessage.content?.artifact?.code?.length,
        hasText: !!newMessage.content?.text,
      });
      
      // Use the conversationId from the mutation variables, not the hook props
      // This is important when a new conversation is created
      messageInsertedConversationUpdate(
        queryClient,
        newMessage,
        variables.conversationId,
      );
      
      // Also invalidate to force a refresh from the database
      // This ensures we have the latest data even if streaming missed something
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['messages', variables.conversationId] });
      }, 500);
    },
    onError: async (error, { messageId, conversationId: errorConvId }) => {
      console.error('Chat error:', error);
      try {
        const errorMessage = await insertMessageAsync({
          role: 'assistant',
          content: {
            text: 'An error occurred while processing your request.',
          },
          parent_message_id: messageId,
          conversation_id: errorConvId, // Use the conversationId from mutation variables
        });
        // Update cache with error message
        messageInsertedConversationUpdate(queryClient, errorMessage, errorConvId);
      } catch (insertError) {
        console.error('Failed to insert error message:', insertError);
      }
    },
  });
}

export function useSendContentMutation({
  conversation,
}: {
  conversation: Pick<
    Conversation,
    'id' | 'current_message_leaf_id' | 'user_id'
  >;
}) {
  const { mutateAsync: insertMessageAsync } = useInsertMessageMutation();

  const { mutateAsync: sendToParametricChat } = useParametricChatMutation({
    conversationId: conversation.id,
  });

  return useMutation({
    mutationKey: ['send-content', conversation.id],
    mutationFn: async (content: Content) => {
      const userMessage = await insertMessageAsync({
        role: 'user',
        content,
        parent_message_id: conversation.current_message_leaf_id ?? null,
        conversation_id: conversation.id,
      });

      await sendToParametricChat({
        model: content.model ?? 'google/gemini-3-flash-preview',
        messageId: userMessage.id,
        conversationId: conversation.id,
        thinking: content.thinking,
      });
    },
  });
}

export function useIsLoading(conversationId: string | null) {
  const isSendingChat = useIsMutating({
    mutationKey: ['parametric-chat', conversationId],
  });
  const isSendingMessage = useIsMutating({
    mutationKey: ['send-content', conversationId],
  });
  return !!isSendingChat || !!isSendingMessage;
}
